import { TAROT_PHASE_LABELS, TAROT_READING_TITLES, tarotNextStep, tarotPhaseCopy, type TarotPreviewMode as TarotMode } from './lib/tarotPreview';
import type { Root } from 'react-dom/client';

const drawButton = document.querySelector<HTMLButtonElement>('#tarot-draw');

if (drawButton) {
  let firstValueRoot: Root | null = null;
  let drawOrdinal = 0;
  const threeModeHint = document.querySelector<HTMLElement>('[data-mode="three"] small');
  if (threeModeHint) threeModeHint.textContent = 'past · present · next step';
  const threePath = document.querySelector<HTMLElement>('.tarot-paths a[href="/en/tarot/three-card.html"] p');
  if (threePath) threePath.textContent = 'Review a past pattern, examine the present, and find a next step to test.';

  const mode = () => (document.querySelector<HTMLButtonElement>('[data-mode].is-active')?.dataset.mode || 'one') as TarotMode;
  const labels = TAROT_PHASE_LABELS;

  function predictionFor(currentMode: TarotMode, cards: HTMLElement[]) {
    if (currentMode === 'yesno') {
      const answer = document.querySelector('#tarot-result-title')?.textContent?.split('—')[0].trim().toLowerCase() || 'pause';
      return tarotNextStep(currentMode, '', answer);
    }
    const finalMeaning = cards.at(-1)?.querySelector<HTMLParagraphElement>('div > p:not(.adj-kicker)')?.textContent?.trim() || 'the final card’s theme';
    return tarotNextStep(currentMode, finalMeaning);
  }

  async function enhanceResult() {
    const result = document.querySelector<HTMLElement>('#tarot-result');
    if (!result || result.hidden) return;
    const currentMode = mode();
    const cards = [...result.querySelectorAll<HTMLElement>('.tarot-card')];
    if (!cards.length) return;
    drawOrdinal += 1;
    const thisDraw = drawOrdinal;
    const phaseLabels = labels[currentMode];

    cards.forEach((card, index) => {
      card.tabIndex = -1;
      const phase = phaseLabels[index] || phaseLabels[0];
      const faceLabel = card.querySelector<HTMLElement>('.tarot-face small');
      const kicker = card.querySelector<HTMLElement>('.adj-kicker');
      if (faceLabel) faceLabel.textContent = phase;
      if (kicker) {
        const orientation = kicker.textContent?.includes('REVERSED') ? 'REVERSED' : 'UPRIGHT';
        kicker.textContent = `${phase} · ${orientation}`;
      }
    });

    let plainReading = result.querySelector<HTMLElement>('.tarot-plain-reading');
    if (!plainReading) {
      plainReading = document.createElement('section');
      plainReading.className = 'tarot-plain-reading';
      result.querySelector('.tarot-synthesis')?.before(plainReading);
    }
    const phaseCopy = cards.map((card, index) => {
      const phase = phaseLabels[index] || phaseLabels[0];
      const name = card.querySelector('h3')?.textContent || 'This card';
      const meaning = card.querySelector<HTMLParagraphElement>('div > p:not(.adj-kicker)')?.textContent?.trim() || '';
      const copy = tarotPhaseCopy(currentMode, index, name, meaning);
      return `<article><small>${phase}</small><h3>${name}</h3><p>${copy}</p></article>`;
    }).join('');
    plainReading.innerHTML = `<header><p class="adj-kicker">PLAIN-LANGUAGE READING</p><h2>${TAROT_READING_TITLES[currentMode]}</h2></header><div>${phaseCopy}</div><aside><small>A NEXT STEP TO TEST</small><p>${predictionFor(currentMode, cards)}</p><b>Entertainment and reflection only · a random draw does not measure probabilities or predict an event.</b></aside>`;

    // Keep initial tarot entry lightweight; the existing reading remains available
    // while the optional guided card loads. Never replace a newer draw with an old one.
    const [{ createElement }, { createRoot }, { FirstValueJourney }] = await Promise.all([
      import('react'), import('react-dom/client'), import('./FirstValueJourney'),
    ]);
    if (thisDraw !== drawOrdinal || !result.isConnected) return;
    firstValueRoot?.unmount();
    const firstValueMount = document.createElement('div');
    // The card reveal is tarot's first reward; reflection follows rather than
    // hiding the drawn symbols behind an onboarding form.
    plainReading.before(firstValueMount);
    firstValueRoot = createRoot(firstValueMount);
    const firstCard = cards[0];
    const meaning = firstCard.querySelector<HTMLParagraphElement>('div > p:not(.adj-kicker)')?.textContent?.trim() || '';
    const cardName = firstCard.querySelector('h3')?.textContent || 'Your card';
    firstValueRoot.render(createElement(FirstValueJourney, {
      key: drawOrdinal, locale: 'en', system: 'tarot', resultOrdinal: drawOrdinal,
      headline: `${cardName} · a prompt to consider`,
      insight: tarotPhaseCopy(currentMode, 0, cardName, meaning),
      evidence: ['Random draw from 22 Major Arcana—not a verified forecast.', ...cards.map(card => `${card.querySelector('h3')?.textContent || 'Card'} · ${card.querySelector('.adj-kicker')?.textContent || 'Reflection prompt'}`)],
      action: predictionFor(currentMode, cards),
      onExplore: () => { firstCard.scrollIntoView({ block: 'start' }); firstCard.focus({ preventScroll: true }); },
    }));

    trackAnalytics('tarot_timeline_viewed', {
      page_type: 'tarot_tool', tool_type: 'tarot', spread_mode: currentMode, card_count: cards.length,
    });
  }

  drawButton.addEventListener('click', () => window.requestAnimationFrame(() => { void enhanceResult().catch(() => { /* The free cards and reading stay usable if guidance cannot load. */ }); }));
}
import { trackAnalytics } from './lib/analytics';
